import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { Modal, Skeleton, message } from 'antd';
import { journalSteps, TradingAccount, tradingAccounts, type StepKey } from 'mock/journalDay.data';
import { C, JdSelect } from '../../components/ui';
import StepStartOfDay from './StepStartOfDay';
import StepBeforeEntry from './StepBeforeEntry';
import StepInTrade from './StepInTrade';
import StepPostTrade from './StepPostTrade';
import StepEndOfDay from './StepEndOfDay';
import './../style/theme.scss';
import { getAccountList, getDailyChecklist } from './function';
import { DynamicChecklists, FormConfigs, Journal, UILabels, StepRefHandle, IncompleteSection } from './type';
import StepEndOfDay_v2 from './StepEndOfDay_v2';
import StepInTradeV2 from './StepInTrade_v2';
import StepPostTradeV2 from './StepPostTrade_v2';
import { ArrowLeftIcon, LockIcon } from '@phosphor-icons/react';
import { PAIR_OPTIONS } from './pairs';

const WEEKDAYS_VI = ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];

// Step stepper segment icons
const STEP_ICONS = [
  <svg key="0" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 19h18" /><path d="M7 19a5 5 0 0 1 10 0" /><path d="M12 3v4M4.5 9.5l1.4 1.4M19.5 9.5l-1.4 1.4" /></svg>,
  <svg key="1" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6v3H9z" /><rect x="5" y="5" width="14" height="16" rx="2" /><path d="M9 13l2 2 4-4" /></svg>,
  <svg key="2" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></svg>,
  <svg key="3" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M5 4a2 2 0 0 1 2-2h11v20H7a2 2 0 0 1-2-2z" /><path d="M9 7h6M9 11h5" /></svg>,
  <svg key="4" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="5" /><path d="M8.5 12.5L7 22l5-3 5 3-1.5-9.5" /></svg>,
];

interface DateEndedAlertProps {
  date: string; // Truyền ngày vào đây, ví dụ: "02/08/2026"
  onBackToToday?: () => void; // Hàm xử lý khi bấm nút "Quay lại hôm nay"
}

export const DateEndedAlert: React.FC<DateEndedAlertProps> = ({
  date,
  onBackToToday,
}) => {
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-[#F3E2B4] bg-[#FFFBEB] p-[24px] text-[#5C4212] shadow-sm">
      {/* Tiêu đề với Icon Khóa */}
      <div className="flex items-center gap-1 font-bold text-[#633C02]">
        <LockIcon size={24} />
        <span className="text-base">
          Ngày <span className="font-extrabold">{new Date(date).toLocaleDateString('vi-VN', { year: 'numeric', month: '2-digit', day: '2-digit' })}</span> đã kết thúc
        </span>
      </div>

      {/* Nội dung thông báo */}
      <p className="text-sm leading-relaxed text-[#785923]">
        Bạn không thể chỉnh sửa hoặc thêm dữ liệu cho những ngày đã quá 5 ngày, dữ liệu chỉ có thể nhập hoặc chỉnh sửa trong vòng 5 ngày gần nhất.
      </p>

      {/* Nút Quay lại hôm nay */}
      <div className="mt-0">
        <button
          onClick={onBackToToday}
          className="inline-flex items-center gap-1 !rounded-lg border border-[#D3C096] bg-white !px-5 py-2 !text-sm font-semibold text-[#4A320A] shadow-xs transition-all hover:bg-[#F9F6EE] active:scale-95"
        >
          <ArrowLeftIcon size={24} />
          Quay lại hôm nay
        </button>
      </div>
    </div>
  );
};

function AccountPicker({
  account,
  setAccount,
  tradingAccountsList
}: {
  account: number;
  setAccount: (val: number) => void;
  tradingAccountsList: TradingAccount[]
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const acc = tradingAccountsList[account];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = tradingAccountsList.filter(a =>
    a.name.toLowerCase().includes(search.toLowerCase()) ||
    a.id.toLowerCase().includes(search.toLowerCase()) ||
    (a.broker && a.broker.toLowerCase().includes(search.toLowerCase()))
  );

  const platMeta: Record<string, { label: string; full: string; bg: string; bd: string; tx: string }> = {
    mt5: { label: 'M5', full: 'MT5', bg: '#FFEDD5', bd: '#FED7AA', tx: '#C2410C' },
    mt4: { label: 'M4', full: 'MT4', bg: '#E0F2FE', bd: '#BAE6FD', tx: '#0369A1' },
    ct: { label: 'CT', full: 'cTrader', bg: '#DCFCE7', bd: '#BBF7D0', tx: '#15803D' },
  };

  const groups = ['mt5', 'mt4', 'ct']
    .map(plat => ({
      plat,
      meta: platMeta[plat],
      items: filtered.filter(a => (a.plat || 'mt5') === plat)
    }))
    .filter(g => g.items.length > 0);

  const currentPlat = acc?.plat || 'mt5';
  const currentMeta = platMeta[currentPlat];

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%', maxWidth: 440 }}>
      {/* Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 14,
          background: '#fff', border: `1px solid ${currentMeta.bd}`, borderRadius: 12,
          padding: '12px 16px', cursor: 'pointer', textAlign: 'left',
          color: '#000', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', transition: 'all .2s ease',
          outline: 'none'
        }}
      >
        <div style={{
          width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 800, flex: 'none', background: currentMeta.bg, color: currentMeta.tx,
          border: `1px solid ${currentMeta.bd}`
        }}>
          {acc ? currentMeta.label : '--'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: -0.2, color: '#000', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {acc?.name || 'Chọn tài khoản'}
          </div>
          <div style={{ fontSize: 12, color: '#64748B', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: currentMeta.tx }} />
            #{acc?.id} · {acc?.broker}
          </div>
        </div>
        <div style={{ textAlign: 'right', flex: 'none', marginRight: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#000' }}>${acc?.balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          <div style={{ fontSize: 11, fontWeight: 500, color: '#94A3B8' }}>Số dư</div>
        </div>
        <svg style={{ flex: 'none', color: '#94A3B8', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
      </button>

      {/* Panel */}
      {isOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0,
          background: '#fff', border: `1px solid ${C.borderSoft}`, borderRadius: 14,
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', zIndex: 1000, overflow: 'hidden'
        }}>
          <div style={{ padding: 12, borderBottom: `1px solid ${C.divider}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '10px 14px' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
              <input
                autoFocus
                placeholder="Tìm tên, ID, sàn..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ flex: 1, background: 'transparent', border: 0, outline: 0, color: '#000', fontSize: 13, fontWeight: 500 }}
              />
            </div>
          </div>
          <div style={{ maxHeight: 350, overflowY: 'auto', padding: '6px 6px 10px' }}>
            {groups.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: '#64748B', fontSize: 13 }}>Không tìm thấy tài khoản</div>
            ) : groups.map(group => (
              <React.Fragment key={group.plat}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: group.meta.tx, padding: '12px 12px 6px', opacity: 0.8 }}>
                  {group.meta.full} · {group.items.length}
                </div>
                {group.items.map((item) => (
                  <div
                    key={item.index}
                    onClick={() => { setAccount(item.index); setIsOpen(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10,
                      cursor: 'pointer', background: item.index === account ? group.meta.bg : 'transparent',
                      transition: 'background .15s ease'
                    }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 800, background: group.meta.bg, color: group.meta.tx,
                      border: `1px solid ${group.meta.bd}`
                    }}>
                      {group.meta.label}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#000', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {item.name}
                        {item.index === account && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={group.meta.tx} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748B', fontWeight: 500, marginTop: 1 }}>
                        #{item.id} · {item.broker}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flex: 'none' }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#000' }}>${item.balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DayDetail({
  date, step, onBack, onStepChange, accountId, onAccountChange
}: {
  date: string;
  step: StepKey;
  onBack: () => void;
  onStepChange: (step: StepKey) => void;
  accountId?: string | null;
  onAccountChange?: (accountId: string) => void;
}) {
  const [account, setAccount] = useState(() => {
    return parseInt(localStorage.getItem('checklist_selected_account') || '0', 10);
  });
  const [tradingAccountsList, setTradingAccountsList] = useState<TradingAccount[]>([]);
  const [rawApiAccounts, setRawApiAccounts] = useState<any[]>([]);
  const acc = tradingAccountsList[account] || tradingAccountsList[0] || { id: '', name: '', pair: 'XAUUSD', broker: '', stage: 'Sinh tồn', balance: 0, currency: 'USD', plat: 'mt5', status: 'live', index: 0 };
  const [journal_data, setJournalData] = useState<Journal | null>(null);
  const [form_configs, setFormConfigs] = useState<FormConfigs | null>(null);
  const [ui_labels, setUiLabels] = useState<UILabels | null>(null);
  const [dynamic_checklists, setDynamicChecklists] = useState<DynamicChecklists | null>(null);

  const today = dayjs().format('YYYY-MM-DD');
  const isToday = date === today;
  const diffDays = dayjs(today).startOf('day').diff(dayjs(date).startOf('day'), 'day');
  const canEdit = diffDays >= 0 && diffDays <= 5;
  // Past days are not necessarily "recorded" (the calendar may show them as missed), so only say they are over
  const dayStatusText = isToday ? 'Đang thực hiện' : date < today ? 'Đã qua' : 'Sắp tới';
  const stColor = isToday
    ? { bg: C.infoBg, bd: C.infoBd, num: C.infoTx, ac: C.infoAc }
    : date < today
      ? { bg: C.okBg, bd: C.okBd, num: C.okTx, ac: C.green }
      : { bg: 'var(--jd-surface-4)', bd: C.futureBd, num: C.futureTx, ac: C.futureAc };

  const stageGold = acc?.stage === 'Sinh tồn' || false;
  const d = dayjs(date);
  const activeStep = journalSteps[step];

  const stepRef = useRef<StepRefHandle>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [incompleteSections, setIncompleteSections] = useState<IncompleteSection[]>([]);
  const [pendingStep, setPendingStep] = useState<StepKey | 'back' | null>(null);

  const attemptStepChange = (targetStep: StepKey) => {
    if (targetStep === step) return;

    if ((step === 0 || step === 1) && canEdit) {
      // Chỉ kiểm tra incomplete khi chuyển tiến (targetStep > step)
      // Khi quay lại bước trước (targetStep < step), luôn cho phép quay lại
      if (targetStep > step) {
        const missing = stepRef.current?.getIncompleteSections?.() || [];
        if (missing.length > 0) {
          stepRef.current?.validateAndHighlight?.();
          setPendingStep(targetStep);
          setIncompleteSections(missing);
          setShowIncompleteModal(true);
          return;
        }
      }

      if (stepRef.current?.hasUnsavedChanges()) {
        setPendingStep(targetStep);
        setShowUnsavedModal(true);
        return;
      }
    }

    onStepChange(targetStep);
  };

  const handleNextStep = () => {
    attemptStepChange((step + 1) as StepKey);
  };

  const handleBack = () => {
    if ((step === 0 || step === 1) && canEdit && stepRef.current?.hasUnsavedChanges()) {
      setPendingStep('back');
      setShowUnsavedModal(true);
      return;
    }
    onBack();
  };

  const handleCancelIncomplete = () => {
    setShowIncompleteModal(false);
    setPendingStep(null);
    setIncompleteSections([]);
  };

  const handleConfirmSkipIncomplete = () => {
    setShowIncompleteModal(false);
    setIncompleteSections([]);

    if (stepRef.current?.hasUnsavedChanges()) {
      setShowUnsavedModal(true);
    } else {
      if (pendingStep === 'back') {
        setPendingStep(null);
        onBack();
      } else {
        const next = pendingStep !== null ? pendingStep : ((step + 1) as StepKey);
        setPendingStep(null);
        onStepChange(next);
      }
    }
  };

  const handleConfirmSave = async () => {
    setIsSavingAll(true);
    try {
      const success = await stepRef.current?.saveChanges();
      if (success !== false) {
        setShowUnsavedModal(false);
        if (pendingStep === 'back') {
          setPendingStep(null);
          onBack();
        } else {
          const next = pendingStep !== null ? pendingStep : ((step + 1) as StepKey);
          setPendingStep(null);
          onStepChange(next);
        }
      } else {
        message.error('Lưu thay đổi thất bại. Vui lòng thử lại!');
      }
    } catch (error) {
      console.error('Error saving unsaved changes:', error);
      message.error('Lưu thay đổi thất bại. Vui lòng thử lại!');
    } finally {
      setIsSavingAll(false);
    }
  };

  const handleCancelSave = () => {
    setShowUnsavedModal(false);
    if (pendingStep === 'back') {
      setPendingStep(null);
      onBack();
    } else {
      const next = pendingStep !== null ? pendingStep : ((step + 1) as StepKey);
      setPendingStep(null);
      onStepChange(next);
    }
  };

  const [loadError, setLoadError] = useState(false);

  const Init = async () => {
    setLoadError(false);
    try {
      const [data, accountList] = await Promise.all([getDailyChecklist(date), getAccountList()]);
      setJournalData(data.journal);
      setFormConfigs(data.form_configs);
      setUiLabels(data.ui_labels);
      setDynamicChecklists(data.dynamic_checklists);
      setRawApiAccounts(accountList);
    } catch (error) {
      console.error(error);
      setLoadError(true);
    }
  }

  // A link with ?account= (reload, shared link, back from another tab) wins over the last index in localStorage,
  // which can point at a different account once the list order changes
  const urlAccountApplied = useRef(false);
  useEffect(() => {
    if (tradingAccountsList.length === 0) return;
    if (!urlAccountApplied.current) {
      urlAccountApplied.current = true;
      const idx = accountId ? tradingAccountsList.findIndex((a) => a.id === accountId) : -1;
      if (idx >= 0 && idx !== account) {
        setAccount(idx);
        return;
      }
    }
    localStorage.setItem('checklist_selected_account', String(account));
    const acc = tradingAccountsList[account];
    if (acc) {
      onAccountChange?.(acc.id);
    }
  }, [account, tradingAccountsList, onAccountChange, accountId]);

  useEffect(() => {
    const mapped = rawApiAccounts.map((acc: any, index: number) => ({
      id: acc.account_number || '',
      name: acc.nickname || 'Unknown',
      pair: "XAUUSD",
      broker: acc.broker || 'N/A',
      stage: acc.current_stage || "Sinh tồn",
      balance: acc.balance || 0,
      currency: acc.currency || 'USD',
      plat: (acc.platform?.toLowerCase().includes('ctrader') ? 'ct' : acc.platform?.toLowerCase().includes('mt4') ? 'mt4' : 'mt5') as any,
      status: (acc.is_active ? 'live' : 'off') as any,
      index: index,
    }));

    setTradingAccountsList(mapped);
  }, [rawApiAccounts]);

  useEffect(() => {
    Init();
  }, [date]);

  // Changing step kept the old scroll position, so the new step opened half-way down the page
  const stepperRef = useRef<HTMLElement>(null);
  const isFirstStepRender = useRef(true);
  useEffect(() => {
    if (isFirstStepRender.current) {
      isFirstStepRender.current = false;
      return;
    }
    stepperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [step]);

  const handlePairChange = (newPair: string) => {
    const newList = [...tradingAccountsList];
    if (newList[account]) {
      newList[account] = { ...newList[account], pair: newPair };
      setTradingAccountsList(newList);
    }
  };

  return (
    <main className="jd-root" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Back + title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '2px 2px 0' }}>
        <button onClick={handleBack} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 10, border: `1px solid ${C.borderSoft}`, background: C.surface, color: C.labelStrong, cursor: 'pointer' }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1.8, textTransform: 'uppercase', color: C.faint, marginBottom: 5 }}>13W Thử thách · Nhật ký giao dịch</div>
          <div className="jd-title-row" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: -.4, color: C.ink, margin: 0 }}>{WEEKDAYS_VI[d.day()]}, {d.format('DD/MM/YYYY')}</h2>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', background: stColor.bg, color: stColor.num, border: `1px solid ${stColor.bd}` }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: stColor.ac }} />{dayStatusText}
            </span>
          </div>
        </div>
      </div>

      {/* Account selector */}
      <section className="jd-card jd-account" style={{ background: C.surface, border: `1px solid ${C.borderSoft}`, borderRadius: 16, padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          {step === 0 ? (
            // Start of day is saved per day, not per account: show that it covers every account instead of a
            // single-account picker that made users repeat the plan for each account
            <div className="jd-account__picker" style={{ flex: '1 1 320px', minWidth: 0, maxWidth: 560 }}>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: C.faint, marginBottom: 8 }}>Áp dụng cho tất cả tài khoản</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {tradingAccountsList.map((a) => (
                  <span key={a.index} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, border: `1px solid ${C.borderSoft}`, background: 'var(--jd-surface-3)', fontSize: 12.5, fontWeight: 600, color: C.labelStrong }}>
                    {a.name}<span style={{ color: C.faint, fontWeight: 500 }}>#{a.id}</span>
                  </span>
                ))}
              </div>
              <div style={{ fontSize: 12.5, color: C.muted, marginTop: 8 }}>Kế hoạch đầu ngày điền một lần cho cả ngày. Các bước sau ghi riêng theo từng tài khoản.</div>
            </div>
          ) : (
            <div className="jd-account__picker" style={{ flex: 'none' }}>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: C.faint, marginBottom: 8 }}>Tài khoản giao dịch</div>
              <AccountPicker
                account={account}
                setAccount={setAccount}
                tradingAccountsList={tradingAccountsList}
              />
            </div>
          )}
          <div className="jd-account__divider" style={{ width: 1, alignSelf: 'stretch', background: C.divider, margin: '4px 0' }} />
          <div className="jd-account__meta" style={{ display: 'flex', alignItems: 'center', gap: 36, flexWrap: 'wrap' }}>
            <div style={{ flex: 'none' }}>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: C.faint, marginBottom: 8 }}>Cặp sản phẩm</div>
              <JdSelect
                value={acc?.pair || ''}
                onChange={handlePairChange}
                options={PAIR_OPTIONS}
                showSearch
                allowCustom
                style={{ minWidth: 160 }}
              />
            </div>
            {step !== 0 && <AccountStat label="ID Tài khoản" value={acc?.id || 'N/A'} />}
            <div style={{ display: step === 0 ? 'none' : undefined }}>
              <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: .5, textTransform: 'uppercase', color: C.uppercaseFaint, marginBottom: 6 }}>Giai đoạn</div>
              <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 13, fontWeight: 700, padding: '5px 13px', borderRadius: 999, background: stageGold ? C.goldStageBg : C.infoBg, color: stageGold ? C.goldStageTx : C.infoTx }}>{acc?.stage || 'N/A'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stepper */}
      <section ref={stepperRef} className="jd-card" style={{ background: C.surface, border: `1px solid ${C.borderSoft}`, borderRadius: 16, padding: 18, scrollMarginTop: 80 }}>
        <div style={{ height: 5, borderRadius: 99, background: C.divider, overflow: 'hidden', margin: '0 2px 16px' }}>
          <div style={{ height: '100%', borderRadius: 99, background: 'linear-gradient(90deg,#E2C079,#C99A3F)', width: `${(step / 4) * 100}%`, transition: 'width .35s' }} />
        </div>
        <div className="jd-stepper" style={{ display: 'flex', gap: 9 }}>
          {journalSteps.map((s, i) => {
            const active = i === step;
            return (
              <div key={i} className="jd-step" onClick={() => attemptStepChange(i as StepKey)}
                style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', borderRadius: 13, cursor: 'pointer', transition: 'all .2s', background: active ? C.stepperActiveBg : 'var(--jd-surface-4)', border: active ? `1.5px solid ${C.gold}` : '1.5px solid transparent', boxShadow: active ? '0 8px 22px -16px rgba(201,154,63,0.7)' : 'none' }}>
                <div style={{ width: 36, height: 36, flex: 'none', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .2s', background: active ? 'linear-gradient(135deg,#E2C079,#C99A3F)' : 'var(--jd-surface-4)', color: active ? '#0B1521' : C.faint }}>
                  {STEP_ICONS[i]}
                </div>
                <div className="jd-step__text" style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25, color: active ? C.ink : C.muted }}>{s.label.split(' — ')[0]}</div>
                  <div className="jd-step__sub" style={{ fontSize: 13, fontWeight: 500, marginTop: 2, lineHeight: 1.25, color: active ? C.goldDark : C.uppercaseFaint }}>{s.sub}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Step content: loading / error placeholders so the page is never left blank under the stepper */}
      {!(journal_data && form_configs && ui_labels && dynamic_checklists) && (
        <section className="jd-card" style={{ background: C.surface, border: `1px solid ${C.borderSoft}`, borderRadius: 16, padding: '26px 28px' }}>
          {loadError ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '12px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: C.muted }}>Không tải được nhật ký ngày này. Vui lòng thử lại.</div>
              <button onClick={Init} style={{ padding: '9px 18px', border: `1px solid ${C.borderSoft}`, borderRadius: 10, background: C.surface, color: C.labelStrong, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Thử lại</button>
            </div>
          ) : (
            <Skeleton active paragraph={{ rows: 6 }} />
          )}
        </section>
      )}
      {journal_data && form_configs && ui_labels && dynamic_checklists && (
        <>
          {/* Only past days are locked as "ended"; future days are just not open yet */}
          {!canEdit && diffDays > 5 && (
            <DateEndedAlert date={date} onBackToToday={() => {
              const d = new Date();
              const todayStr = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
              window.location.href = `/academy?tab=checklist&date=${todayStr}&step=0&account=${acc.id}`;
            }
            } />

          )}
          {diffDays < 0 && (
            <div style={{ borderRadius: 12, border: `1px solid ${C.infoBd}`, background: C.infoBg, color: C.infoTx, padding: '14px 18px', fontSize: 14, fontWeight: 600 }}>
              Ngày này chưa tới — bạn chỉ có thể xem trước nội dung, việc nhập liệu sẽ mở vào đúng ngày.
            </div>
          )}
          <section className="jd-card" style={{ background: C.surface, border: `1px solid ${C.borderSoft}`, borderRadius: 16, padding: '26px 28px' }}>
            <div className="jd-step-header" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
              <div style={{ width: 40, height: 40, borderRadius: 11, background: 'var(--jd-badge-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--jd-badge-tx)', fontWeight: 800, fontSize: 16 }}>{step + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.title }}>{activeStep.label}</div>
                <div style={{ fontSize: 13, color: C.muted, marginTop: 1 }}>{activeStep.desc}</div>
              </div>
              <span className="jd-step-header__chip" style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px', borderRadius: 999, fontSize: 13, fontWeight: 700, background: C.goldPillBg, color: C.goldDark, border: `1px solid ${C.goldPillBd}` }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" /></svg>{activeStep.timing}
              </span>
            </div>

            {step === 0 && <StepStartOfDay ref={stepRef} onReload={Init} date={date} account_number={acc.id} symbol={acc.pair} journal_data={journal_data} form_configs={form_configs.daily_plan} ui_labels={ui_labels.daily_plan} can_edit={canEdit} />}
            {step === 1 && <StepBeforeEntry ref={stepRef} date={date} account_number={acc.id} symbol={acc.pair} journal_data={journal_data} dynamic_checklist={dynamic_checklists} form_configs={form_configs.pre_trade} ui_labels={ui_labels.pre_trade} onReload={Init} can_edit={canEdit} />}
            {step === 2 && <StepInTradeV2 date={date} account_number={acc.id} symbol={acc.pair} journal_data={journal_data} dynamic_checklist={dynamic_checklists} form_configs={form_configs.in_trade} ui_labels={ui_labels.in_trade} onReload={Init} can_edit={canEdit} />}
            {step === 3 && <StepPostTradeV2 date={date} account_number={acc.id} symbol={acc.pair} journal_data={journal_data} form_configs={form_configs} onReload={Init} can_edit={canEdit} />}
            {/* {step === 4 && <StepEndOfDay date={date} journal_data={journal_data} form_configs={form_configs.daily_plan} ui_labels={ui_labels.daily_plan} onReload={Init} />} */}
            {step === 4 && <StepEndOfDay_v2 date={date} account_number={acc.id} symbol={acc.pair} journal_data={journal_data} form_configs={form_configs} ui_labels={ui_labels} onReload={Init} can_edit={canEdit} onGoToStep={(s) => attemptStepChange(s as StepKey)} />}
            {/* Nav */}
            <div className="jd-step-nav" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 26, paddingTop: 20, borderTop: `1px solid ${C.divider}` }}>
              <button onClick={() => {
                if (step > 0) {
                  attemptStepChange((step - 1) as StepKey);
                } else {
                  handleBack();
                }
              }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 18px', border: `1px solid ${C.borderSoft}`, borderRadius: 11, background: C.surface, color: C.labelStrong, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>{step === 0 ? <span>Quay lại<span className="jd-hide-sm"> danh sách</span></span> : 'Bước trước'}
              </button>
              <span className="jd-step-nav__counter" style={{ fontSize: 13, fontWeight: 600, color: C.faint }}>Bước {step + 1} / 5</span>
              {step === 4 ? (
                // <button style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', border: 'none', borderRadius: 11, background: 'linear-gradient(135deg,#E2C079,#C99A3F)', color: '#0B1521', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                //   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 6" /></svg>Hoàn tất & lưu nhật ký
                // </button>
                <div>
                </div>
              ) : (
                <button onClick={handleNextStep}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', border: 'none', borderRadius: 11, background: C.ink, color: C.surface, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Bước tiếp theo<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
                </button>
              )}
            </div>
          </section>
        </>
      )}

      {/* Incomplete Data Confirmation Modal */}
      <Modal
        open={showIncompleteModal}
        onCancel={handleCancelIncomplete}
        footer={null}
        centered
        width={460}
        styles={{
          content: {
            borderRadius: 16,
            padding: '24px',
            border: `1px solid ${C.borderSoft}`,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12, flex: 'none',
              background: '#FFFBEB', border: '1px solid #F3E2B4',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#D97706',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.title, lineHeight: 1.3 }}>
                Thông tin chưa hoàn thành
              </h3>
              <p style={{ margin: '8px 0 0', fontSize: 14, color: C.muted, lineHeight: 1.5 }}>
                Các phần sau vẫn chưa được điền đầy đủ:
              </p>
            </div>
          </div>

          <div style={{
            background: 'var(--jd-surface-3)',
            borderRadius: 12,
            padding: '14px 16px',
            border: `1px solid ${C.borderSoft}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 9,
            maxHeight: 220,
            overflowY: 'auto'
          }}>
            {incompleteSections.map((item, idx) => (
              <div key={idx} style={{ fontSize: 13.5, fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 22, height: 22, padding: '0 6px', borderRadius: 999,
                  background: '#FEF3C7', color: '#D97706', fontSize: 12, fontWeight: 800, flex: 'none'
                }}>
                  {item.num}
                </span>
                <span>{item.title}</span>
              </div>
            ))}
          </div>

          <p style={{ margin: '4px 0 0', fontSize: 14, fontWeight: 600, color: C.title }}>
            Bạn có thể sang bước sau và quay lại hoàn thành sau.
          </p>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
            <button
              onClick={handleCancelIncomplete}
              style={{
                padding: '10px 18px',
                borderRadius: 10,
                border: `1px solid ${C.borderSoft}`,
                background: C.surface,
                color: C.labelStrong,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              Ở lại điền tiếp
            </button>
            <button
              onClick={handleConfirmSkipIncomplete}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 24px',
                borderRadius: 10,
                border: 'none',
                background: 'linear-gradient(135deg, #E2C079, #C99A3F)',
                color: '#0B1521',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(201, 154, 63, 0.3)',
              }}
            >
              Bỏ qua, sang bước sau
            </button>
          </div>
        </div>
      </Modal>

      {/* Unsaved Changes Confirmation Modal */}
      <Modal
        open={showUnsavedModal}
        onCancel={() => !isSavingAll && setShowUnsavedModal(false)}
        footer={null}
        centered
        width={440}
        closable={!isSavingAll}
        maskClosable={!isSavingAll}
        styles={{
          content: {
            borderRadius: 16,
            padding: '24px',

            border: `1px solid ${C.borderSoft}`,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12, flex: 'none',
              background: '#FFFBEB', border: '1px solid #F3E2B4',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#D97706',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.title, lineHeight: 1.3 }}>
                Thay đổi chưa được lưu
              </h3>
              <p style={{ margin: '8px 0 0', fontSize: 14, color: C.muted, lineHeight: 1.5 }}>
                Bạn có muốn lưu các thay đổi không?
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
            <button
              onClick={handleCancelSave}
              disabled={isSavingAll}
              style={{
                padding: '10px 20px',
                borderRadius: 10,
                border: `1px solid ${C.borderSoft}`,
                background: C.surface,
                color: C.labelStrong,
                fontSize: 14,
                fontWeight: 600,
                cursor: isSavingAll ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: isSavingAll ? 0.6 : 1,
              }}
            >
              Tiếp tục, chưa lưu
            </button>
            <button
              onClick={handleConfirmSave}
              disabled={isSavingAll}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 24px',
                borderRadius: 10,
                border: 'none',
                background: 'linear-gradient(135deg, #E2C079, #C99A3F)',
                color: '#0B1521',
                fontSize: 14,
                fontWeight: 700,
                cursor: isSavingAll ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(201, 154, 63, 0.3)',
                opacity: isSavingAll ? 0.7 : 1,
              }}
            >
              {isSavingAll ? (
                <>
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                  Đang lưu...
                </>
              ) : (
                'Lưu & tiếp tục'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </main>
  );
}

function AccountStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: .5, textTransform: 'uppercase', color: C.uppercaseFaint, marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: C.text, letterSpacing: -.3 }}>{value}</div>
    </div>
  );
}
